import { safeFileStem, isConsolidatedParent } from './shipmentPackingList';

/** Letterhead — adjust to match your registered office */
export const MANIFEST_COMPANY = {
  name: 'JET SHIPPING & LOGISTIC SDN BHD',
  address:
    'NO. AZ-B1-07, LOT NO. 1B/2(A), ANZEN BUSINESS PARK, KEPONG, KUALA LUMPUR, 52100',
  email: 'jetshippingandlogistic@gmail.com',
  tel: '+60 11 21190765 / +60 11 60536205',
};

/** Match manifest / Excel letterhead labels (space before colon). */
export const MANIFEST_LABEL = {
  email: 'EMAIL :',
  tel: 'TEL :',
};

/**
 * Split a long address into 2+ lines at natural breaks (Excel-style rows).
 * @param {string} addr
 * @returns {string[]}
 */
export function splitManifestAddressLines(addr) {
  const raw = String(addr || '').trim();
  if (!raw) return [''];

  const sri = raw.match(/^(.*),\s*(SRI LANKA.*)$/i);
  if (sri) {
    const a = sri[1].trim();
    const b = sri[2].trim();
    return [a.endsWith(',') ? a : `${a},`, b];
  }

  const kep = raw.match(/^(.*),\s*(KEPONG\s*,.*)$/i);
  if (kep) {
    const a = kep[1].trim();
    const b = kep[2].trim();
    return [a.endsWith(',') ? a : `${a},`, b];
  }

  if (raw.length <= 54) return [raw];

  let cut = raw.slice(0, 56).lastIndexOf(',');
  if (cut < 24) cut = raw.indexOf(',', 30);
  if (cut > 0 && cut < raw.length - 1) {
    return [raw.slice(0, cut + 1).trim(), raw.slice(cut + 1).trim()].filter(Boolean);
  }

  return [raw];
}

export { safeFileStem as manifestSafeFileStem };

export function parseAgentBlock(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return {
    name: lines[0] || '',
    address: lines[1] || '',
    email: lines[2] || '',
    row3Right: lines[2] || '',
  };
}

/**
 * Manifest letterhead right column from shipment workspace (structured agent or legacy text).
 */
export function agentBlockFromDraft(draft) {
  const r = draft?.shippingAgentRegistration;
  if (r && typeof r === 'object') {
    const has =
      String(r.companyName || '').trim() ||
      String(r.companyId || '').trim() ||
      String(r.email || '').trim() ||
      String(r.address || '').trim() ||
      String(r.primaryMobile || '').trim() ||
      String(r.secondaryMobile || '').trim();
    if (has) {
      const name = String(r.companyName || '').trim() || '—';
      const p = String(r.primaryMobile || '').trim();
      const s = String(r.secondaryMobile || '').trim();
      let telLine = '';
      if (p && s) telLine = `${MANIFEST_LABEL.tel} ${p} / ${s}`;
      else if (p) telLine = `${MANIFEST_LABEL.tel} ${p}`;
      else if (s) telLine = `${MANIFEST_LABEL.tel} ${s}`;
      const em = String(r.email || '').trim();
      const emailLine = em ? `${MANIFEST_LABEL.email} ${em}` : '';
      const row3Right = [emailLine, telLine].filter(Boolean).join('\n');
      return {
        name: name || '—',
        address: String(r.address || '').trim(),
        email: em,
        emailLine,
        telLine,
        row3Right: row3Right || '—',
      };
    }
  }
  const parsed = parseAgentBlock(draft?.shippingAgent || '');
  const em = String(parsed.email || '').trim();
  const emailLine = em ? `${MANIFEST_LABEL.email} ${em}` : '';
  const row3Right = emailLine || '—';
  return {
    name: parsed.name || '—',
    address: parsed.address || '',
    email: em,
    emailLine,
    telLine: '',
    row3Right,
  };
}

function inferCountryFromAddress(addr) {
  const a = String(addr || '');
  if (/sri\s*lanka/i.test(a)) return 'SRI LANKA';
  if (/malaysia/i.test(a)) return 'MALAYSIA';
  return 'MALAYSIA';
}

export function findConsigneeInList(consigneeId, consigneesList) {
  if (!consigneeId || !Array.isArray(consigneesList)) return null;
  const id = String(consigneeId).trim();
  return (
    consigneesList.find(
      (c) =>
        String(c.passportNo || '').trim() === id ||
        String(c.nicNo || '').trim() === id ||
        String(c.drivingLicenceNo || '').trim() === id ||
        String(c._id || c.id || '').trim() === id,
    ) || null
  );
}

export function formatShipperDetails(shipper) {
  if (!shipper) return '—\nMALAYSIA';
  const name = String(shipper.nameWithInitials || '—').trim();
  const country = inferCountryFromAddress(shipper.address);
  return `${name}\n${country}`;
}

export function formatConsigneeDetails(consignee, fallbackConsigneeId) {
  if (!consignee) {
    return fallbackConsigneeId ? `Consignee ID: ${fallbackConsigneeId}` : '—';
  }
  const lines = [
    String(consignee.nameWithInitials || '').trim(),
    String(consignee.address || '').trim(),
    Array.isArray(consignee.mobiles) && consignee.mobiles.length
      ? consignee.mobiles.filter(Boolean).join(', ')
      : '',
    String(
      consignee.passportNo || consignee.nicNo || consignee.drivingLicenceNo || '',
    ).trim(),
  ].filter(Boolean);
  return lines.length ? lines.join('\n') : '—';
}

export function formatManifestDescriptionFromBoxes(boxes) {
  const list = Array.isArray(boxes) ? boxes : [];
  if (!list.length) return '—';
  /** Same merge key as HBL: official → box type → description; sum quantities per label. */
  const totals = new Map();
  const order = [];
  const display = new Map();

  for (const b of list) {
    const q = Number.parseInt(String(b.quantity), 10);
    const qty = Number.isFinite(q) && q > 0 ? q : 1;
    const official = String(b.officialBoxName || '').trim();
    const boxType = String(b.boxType || '').trim();
    const desc = String(b.description || '').trim();
    const labelRaw = official || boxType || desc;
    const key = (labelRaw || 'PACKAGE').toUpperCase();
    if (!totals.has(key)) {
      order.push(key);
      display.set(key, official || boxType || desc || 'PACKAGE');
    }
    totals.set(key, (totals.get(key) || 0) + qty);
    if (official) display.set(key, official);
  }

  return order
    .map((key) => {
      const label = String(display.get(key) || key).toUpperCase();
      return `${totals.get(key)} - ${label}`;
    })
    .join('\n');
}

export function formatUpbGift(collection) {
  const ct = String(collection?.clearingType || '')
    .trim()
    .toUpperCase();
  if (!ct) return '—';
  if (ct.includes('GIFT')) return 'GIFT';
  if (ct.includes('UPB')) return 'UPB';
  return ct;
}

/**
 * @param {string[]} linkedCollectionIds
 * @param {Map<string, object>} collectionById
 * @param {Map<string, object>} shipperByPassportNo
 * @param {object[]} consigneesList
 */
export function buildCargoManifestRows(
  linkedCollectionIds,
  collectionById,
  shipperByPassportNo,
  consigneesList,
) {
  const rows = [];
  let seq = 1;
  const ids = (linkedCollectionIds || []).map(String);

  for (const cid of ids) {
    const c = collectionById.get(cid);
    if (!c) continue;
    if (c.consolidatedInto) continue;

    const passport = String(c.shipperPassportNo || '').trim();
    const shipper = passport ? shipperByPassportNo.get(passport) : null;
    const consignee = findConsigneeInList(c.consigneeId, consigneesList);

    let boxes = Array.isArray(c.boxes) ? c.boxes : [];
    if (isConsolidatedParent(c)) {
      boxes = (c.childCollectionIds || [])
        .map((childId) => collectionById.get(String(childId)))
        .filter(Boolean)
        .flatMap((child) => (Array.isArray(child.boxes) ? child.boxes : []));
    }

    let pcs = 0;
    let cbmTot = 0;
    let kgTot = 0;
    for (const b of boxes) {
      const q = Number.parseInt(String(b.quantity), 10);
      const qty = Number.isFinite(q) && q > 0 ? q : 1;
      pcs += qty;
      const cbm = parseFloat(String(b.cbm || '').replace(/,/g, '')) || 0;
      const w = parseFloat(String(b.weight || '').replace(/,/g, '')) || 0;
      cbmTot += cbm * qty;
      kgTot += w * qty;
    }

    rows.push({
      key: cid,
      no: String(seq),
      hbl: String(c.collectionNo || '').trim(),
      shipperDetails: formatShipperDetails(shipper),
      consigneeDetails: formatConsigneeDetails(consignee, c.consigneeId),
      pcs,
      cbm: cbmTot,
      weight: kgTot,
      description: formatManifestDescriptionFromBoxes(boxes),
      upbGift: formatUpbGift(c),
    });
    seq += 1;
  }

  return rows;
}

export function sumManifestTotals(manifestRows) {
  return (manifestRows || []).reduce(
    (acc, r) => {
      acc.pcs += Number(r.pcs) || 0;
      acc.cbm += Number(r.cbm) || 0;
      acc.weight += Number(r.weight) || 0;
      return acc;
    },
    { pcs: 0, cbm: 0, weight: 0 },
  );
}

export function buildManifestMeta(draft, totals) {
  const vessel = [draft.vesselName, draft.voyageNo].filter(Boolean).join(' / ');
  const container = [draft.containerNo, draft.sealNo].filter(Boolean).join(' / ');
  return {
    billOfLadingNo: draft.billOfLadingNo || '—',
    vessel: vessel || '—',
    container: container || '—',
    totalPackages: String(Math.round(totals.pcs) || totals.pcs),
    totalCbm: (Math.round(totals.cbm * 100) / 100).toFixed(2),
    totalWeightKg: String(Math.round(totals.weight) || Math.round(totals.weight * 100) / 100),
  };
}
